// src/features/estoque/components/ListaEstoque.jsx
import React, { useState, useEffect } from "react";
import { observarEstoque } from "services/estoqueService";
import { Input } from "components/ui/input";
import { Search, PackageOpen } from "lucide-react";

/**
 * Componente que exibe a lista de estoque atualizada em tempo real (onSnapshot).
 */
const ListaEstoque = () => {
  const [estoque, setEstoque] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    // Inicia o listener de Firestore. 
    // A cada mudança no documento da coleção, este callback será ativado pela SDK re-renderizando o componente.
    const unsubscribe = observarEstoque((dadosAtualizados) => {
      setEstoque(dadosAtualizados);
      setLoading(false);
    });

    // Função de limpeza (cleanup) executada quando componente for desmontado, 
    // previnindo listeners mortos pesando em memória.
    return () => unsubscribe();
  }, []);

  // Aplica o filtro sobre o estado mantido localmente
  const estoqueFiltrado = estoque.filter((item) =>
    item.nome.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="bg-white dark:bg-slate-900 shadow rounded-lg border border-gray-200 dark:border-slate-800 p-6 flex flex-col mt-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Produtos Cadastrados
        </h2>
        
        {/* Input de Busca */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Buscar por nome do produto..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="pl-9 w-full"
          />
        </div>
      </div>

      {loading ? (
        // Skeleton / Loading simples
        <div className="space-y-4 animate-pulse">
            <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
        </div>
      ) : estoqueFiltrado.length === 0 ? (
        // Fallback pra vazio
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg">
            Nenhum produto encontrado no estoque.
        </div>
      ) : (
        // Tabela Estilizada (Tailwind + Dark Mode)
        <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-slate-700">
          <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-slate-800 dark:text-gray-300">
              <tr>
                <th scope="col" className="px-6 py-3 font-semibold">Código</th>
                <th scope="col" className="px-6 py-3 font-semibold">Produto</th>
                <th scope="col" className="px-6 py-3 font-semibold text-center">NCM</th>
                <th scope="col" className="px-6 py-3 font-semibold text-right">Qtd. Atual</th>
                <th scope="col" className="px-6 py-3 font-semibold text-right">Última Atualização</th>
              </tr>
            </thead>
            <tbody>
              {estoqueFiltrado.map((item) => (
                <tr 
                    key={item.id} 
                    className="bg-white border-b dark:bg-slate-900 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <td className="px-6 py-4 font-mono text-xs">{item.codigo}</td>
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {item.nome}
                  </td>
                  <td className="px-6 py-4 font-mono text-center">
                    {item.ncm}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`px-2 py-1 rounded font-bold ${item.quantidadeAtual < 10 ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'}`}>
                        {item.quantidadeAtual}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-xs text-gray-400">
                    {item.ultimaAtualizacao}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListaEstoque;
